import React, { useEffect, useState } from "react";
import { Button, Snackbar, TextField, IconButton } from "@mui/material";
import { DndProvider } from 'react-dnd';
import { v4 as uuidv4 } from "uuid";
import WorkingArea from "./WorkingArea";
import PropertiesPanel from "./PropertiesPanel";
import { HTML5Backend } from "react-dnd-html5-backend";
import WidgetPanel from "./WidgetPanel";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import "./CreateForm.css";
import { useLocation, useNavigate } from "react-router-dom";
import { Widgets } from "./Widgets";

type FormWidget = {
  id: string;
  widgetType: string;
  config: Record<string, any>;
  name?: string;
  icon?: string;
  sectionId?: string;
  columnName?: string;
};

type FormSection = {
  id: string;
  name: string;
  widgets: FormWidget[];
};

type initialFormType = {
  api: string;
  sections: FormSection[];
  formName: string;
  buttonLabel: string;
  formId: string;
};

type CreateFormProps = {
  initialForm?: initialFormType | { sections: FormSection[] };
  widgetPanelDisabled?: boolean;
  onSave?: (formData: FormSection[], meta: any) => void;
  onFormChange?: (formData: FormSection[]) => void;
  mandatoryLabels?: string[];
};

const CreateForm: React.FC<CreateFormProps> = ({
  initialForm: propInitialForm,
  widgetPanelDisabled = false,
  onSave,
  onFormChange: propOnFormChange,
  mandatoryLabels = [],
}) => {
  const extractSections = (data: any): FormSection[] => {
    if (!data) return [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
    
    if (Array.isArray(data)) {
      return data.length > 0 ? data : [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
    }
    
    if (data.sections && Array.isArray(data.sections)) {
      return data.sections.length > 0 ? data.sections : [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
    }
    
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return extractSections(parsed);
      } catch (error) {
        console.error("Error parsing sections data:", error);
        return [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
      }
    }
    
    return [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
  };

  const [formSections, setFormSections] = useState<FormSection[]>(() => {
    return [{ id: "section-1", name: "Untitled Section 1", widgets: [] }];
  });
  
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null);

  const location = useLocation();
  const initialForm: initialFormType | null =
    propInitialForm ?? location.state?.initialForm ?? null;
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const savedFormState = localStorage.getItem("formState");
      
      if (initialForm) {
        console.log("Initial form data:", initialForm);
        const sections = extractSections(initialForm);
        console.log("Extracted sections:", sections);
        setFormSections(sections);
      } else if (savedFormState) {
        const parsedState = JSON.parse(savedFormState);
        const sections = extractSections(parsedState);
        setFormSections(sections);
      }
    } catch (error) {
      console.error("Error processing initial form data:", error);
      setFormSections([{ id: "section-1", name: "Untitled Section 1", widgets: [] }]);
    }
  }, [initialForm]);

  const handleSelectWidget = (id: string) => {
    if (!Array.isArray(formSections) || !formSections[currentSectionIndex]) {
      console.error("Invalid formSections or currentSectionIndex");
      return;
    }
    
    const widget =
      formSections[currentSectionIndex]?.widgets.find((w) => w.id === id) || null;
    setSelectedWidgetId(id);
    console.log("Selected widget:", widget);
  };

  const selectedWidget = 
    Array.isArray(formSections) && formSections[currentSectionIndex]
      ? formSections[currentSectionIndex]?.widgets.find((w) => w.id === selectedWidgetId) ?? null
      : null;

  const handleRemoveWidget = (id: string) => {
    if (!Array.isArray(formSections)) return;
    
    setFormSections((prevSections) => {
      if (!Array.isArray(prevSections)) return prevSections;
      
      const updatedSections = [...prevSections];
      if (updatedSections[currentSectionIndex]) {
        updatedSections[currentSectionIndex].widgets = updatedSections[
          currentSectionIndex
        ].widgets.filter((widget) => widget.id !== id);
      }
      
      if (typeof propOnFormChange === "function") {
        propOnFormChange(updatedSections);
      }
      
      return updatedSections;
    });
  };

  const handleDropWidget = (targetFormId: string, droppedWidget: any) => {
    if (!Array.isArray(formSections)) return;
    
    const isNewWidget = !droppedWidget.sectionId;

    const newWidget = {
      ...droppedWidget,
      id: isNewWidget ? uuidv4() : droppedWidget.id,
      widgetType: droppedWidget.widgetType ?? droppedWidget.id,
      config: JSON.parse(JSON.stringify(droppedWidget.config)),
      sectionId: targetFormId,
    };

    const updatedSections = formSections.map((section) => {
      let updatedWidgets = section.widgets;

      // Remove from previous section
      if (section.widgets.some((w) => w.id === droppedWidget.id)) {
        updatedWidgets = updatedWidgets.filter((w) => w.id !== droppedWidget.id);
      }

      // Add to target section
      if (section.id === targetFormId) {
        updatedWidgets = [newWidget, ...updatedWidgets];
      }

      return {
        ...section,
        widgets: updatedWidgets,
      };
    });

    setFormSections(updatedSections);

    if (typeof propOnFormChange === "function") {
      propOnFormChange(updatedSections);
    }
  };

  const handleUpdateWidgetConfig = (id: string, newConfig: Record<string, any>) => {
    if (!Array.isArray(formSections)) return;
    
    const updatedSections = formSections.map((section, index) =>
      index === currentSectionIndex
        ? {
          ...section,
          widgets: section.widgets.map((widget) =>
            widget.id === id
              ? { ...widget, config: { ...widget.config, ...newConfig } }
              : widget
          ),
        }
        : section
    );
    setFormSections(updatedSections);
    if (typeof propOnFormChange === "function") {
      propOnFormChange(updatedSections);
    }
  };

  const handleChangeWidgetType = (widgetId: string, newWidgetType: string) => {
    if (!Array.isArray(formSections)) return;
    
    const widgetTemplate = Widgets.find(w => w.id === newWidgetType);
    if (!widgetTemplate) {
      console.error(`Widget type ${newWidgetType} not found in Widgets array`);
      return;
    }

    const updatedSections = formSections.map((section, index) =>
      index === currentSectionIndex
      ? {
        ...section,
        widgets: section.widgets.map((widget) =>
        widget.id === widgetId
          ? { 
            ...widget, 
            widgetType: newWidgetType,
            config: {
            ...JSON.parse(JSON.stringify(widgetTemplate.config)),
            label: widget.columnName || ''
            }
          }
          : widget
        ),
      }
      : section
    );
    
    setFormSections(updatedSections);
    if (typeof propOnFormChange === "function") {
      propOnFormChange(updatedSections);
    }
  };

  const addSection = () => {
    if (!Array.isArray(formSections)) return;
  
    setFormSections((prev) => {
      if (!Array.isArray(prev)) return prev;
  
      const newSections = [
        ...prev,
        {
          id: uuidv4(),
          name: `Untitled Section ${prev.length + 1}`,
          widgets: [],
        },
      ];
  
      setCurrentSectionIndex(newSections.length - 1);
      if (typeof propOnFormChange === 'function') {
        propOnFormChange(newSections);
      }
  
      return newSections;
    });
  };

  const updateSectionName = (index: number, newName: string) => {
    if (!Array.isArray(formSections)) return;
  
    setFormSections((prevSections) => {
      if (!Array.isArray(prevSections)) return prevSections;
  
      const updatedSections = prevSections.map((section, i) =>
        i === index ? { ...section, name: newName } : section
      );
      if (typeof propOnFormChange === "function") {
        propOnFormChange(updatedSections); 
      }
  
      return updatedSections;
    });
  };

  const deleteSection = (index: number) => {
    if (!Array.isArray(formSections) || formSections.length === 1) return;

    setFormSections((prevSections) => {
      if (!Array.isArray(prevSections)) return prevSections;
      
      const updatedSections = prevSections.filter((_, i) => i !== index);
      let newIndex = currentSectionIndex;
      if (index === currentSectionIndex) {
        newIndex = Math.min(currentSectionIndex, updatedSections.length - 1);
      }
      setCurrentSectionIndex(newIndex);
      
      if (typeof propOnFormChange === "function") {
        propOnFormChange(updatedSections);
      }
      
      return updatedSections;
    });
  };

  const generateJSON = () => {
    if (!Array.isArray(formSections)) {
      setErrorMessage("Invalid form structure.");
      return;
    }
    
    if (formSections.some((section) => section.widgets.length === 0)) {
      setErrorMessage("You cannot proceed with an empty section.");
      return;
    }

    const fullFormConfig = {
      ...initialForm,
      sections: formSections,
    };

    const jsonSchema = JSON.stringify(fullFormConfig, null, 2);
    console.log("Generated Form JSON:", fullFormConfig);

    localStorage.setItem("formState", jsonSchema);

    const meta = {
      api: fullFormConfig.api ?? null,
      formName: fullFormConfig.formName ?? null,
      buttonLabel: fullFormConfig.buttonLabel ?? null,
      formId: fullFormConfig.formId ?? null,
    };

    const targetPath = "/dashboard/manageForms/createForm/chooseApi";
    const statePayload = {
      jsonSchema,
      ...meta,
    };

    if (onSave && widgetPanelDisabled) {
      onSave(formSections, meta);
    } else {
      navigate(targetPath, { state: statePayload });
    }
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const handleReorderWidgets = (sectionId: string, fromIndex: number, toIndex: number) => {
    if (!Array.isArray(formSections)) return;
    
    console.log(`Reordering widgets in section ${sectionId} from ${fromIndex} to ${toIndex}`);

    setFormSections((prevSections) => {
      if (!Array.isArray(prevSections)) return prevSections;
      
      const updatedSections = prevSections.map((section) => {
        if (section.id !== sectionId) return section;

        const widgets = [...section.widgets];
        const [movedWidget] = widgets.splice(fromIndex, 1);
        widgets.splice(toIndex, 0, movedWidget);
        // console.log("Reordered widgets:", widgets.map(w => ({ id: w.id, label: w.config?.label })));
        return {
          ...section,
          widgets,
        };
      });

      console.log("Updated sections after reorder:", updatedSections);
      if (typeof propOnFormChange === "function") {
        propOnFormChange(updatedSections);
      }
      return updatedSections;
    });
  };

  // Safety check to ensure formSections is always an array
  const safeSections = Array.isArray(formSections) ? formSections : [];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="form-container" data-widget-panel-disabled={widgetPanelDisabled}>
        {/* Left Panel */}
        {!widgetPanelDisabled && (
          <div className="left-panel">
            <WidgetPanel />
          </div>
        )}

        {/* Middle Panel */}
        <div className="middle-panel">
          <div className="working-area">
            <div className="section-tabs">
              {safeSections.map((section, index) => (
                <div
                  key={section.id}
                  className={`section-tab ${index === currentSectionIndex ? "active" : ""}`}
                  onClick={() => setCurrentSectionIndex(index)}
                  onDoubleClick={() => setEditingTabIndex(index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (currentSectionIndex !== index) {
                      setCurrentSectionIndex(index);
                    }
                  }}
                >
                  {editingTabIndex === index ? (
                    <TextField
                      value={section.name}
                      onChange={(e) => updateSectionName(index, e.target.value)}
                      onBlur={() => setEditingTabIndex(null)}
                      autoFocus
                      size="small"
                      sx={{ minWidth: 100 }}
                    />
                  ) : (
                    <>
                      <span>{section.name}</span>
                      {safeSections.length > 1 && (
                        <IconButton
                          size="small"
                          className="delete-icon"
                          onClick={() => {
                            deleteSection(index);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  )}
                </div>
              ))}
              <IconButton onClick={addSection} className="add-section" style={{alignContent:"center"}}>
                <AddIcon />
              </IconButton>
            </div>

            <WorkingArea
              formWidgets={safeSections[currentSectionIndex]?.widgets || []}
              onSelectWidget={handleSelectWidget}
              selectedWidgetId={selectedWidgetId}
              updateConfig={handleUpdateWidgetConfig}
              removeWidget={handleRemoveWidget}
              onDropWidget={handleDropWidget}
              formId={safeSections[currentSectionIndex]?.id || ""}
              onReorderWidgets={handleReorderWidgets}
              mandatoryLabels={mandatoryLabels}
            />
            <div className="button-container">
              {!widgetPanelDisabled && (
                <Button variant="contained" color="info" onClick={generateJSON}>
                  Save & Next
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="right-panel">
          <div className="right-panel-content">
            {selectedWidget ? (
              <PropertiesPanel
                widget={selectedWidget}
                updateWidgetConfig={handleUpdateWidgetConfig}
                onWidgetTypeChange={handleChangeWidgetType}
                onClose={() => setSelectedWidgetId(null)}
              />
            ) : (
              <div className="placeholder">Select a widget to edit properties.</div>
            )}
          </div>
        </div>

        {/* Snackbar for error */}
        {errorMessage && (
          <Snackbar
            open={true}
            autoHideDuration={6000}
            onClose={handleCloseError}
            message={errorMessage}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default CreateForm;